import {
  useCallback,
  useContext,
  createContext
} from 'react';

import { decrypt } from '../helpers/cryptography';
import { formatDate } from '../helpers/formatDate';

import { monitoramentoService } from '../api/monitoramentoService';

import { startOfToday } from 'date-fns';
import exportFromJSON from 'export-from-json';

// import { dates } from '../helpers/dates';

const MonitoramentoContext = createContext({});

export const MonitoramentoProvider = ({ children }) => {

  const getAllMonitoramentos = useCallback(async (pageNumber, limit, filterBy, orderBy) => {
    try {
        const response = await monitoramentoService.allMonitoramentos(pageNumber, limit, filterBy, orderBy);

        return response;
    } catch (error) {
        console.log(error);
    }
  }, []);

  const getMonitoramentoById = useCallback(async (id) => {
    try {
        const response = await monitoramentoService.getMonitoramento(id);

        const decryptedResponse = {
            ...response,
            usuario: response.usuario ? decrypt(response.usuario) : '',
            nomeUsuario: response.nomeUsuario ? decrypt(response.nomeUsuario) : '',
            emailRemetente: response.emailRemetente ? decrypt(response.emailRemetente) : '',
            remetente: response.remetente ? decrypt(response.remetente) : '',
            outroRemetente: response.outroRemetente ? decrypt(response.outroRemetente) : '',
            emailDestinatario: response.emailDestinatario ? decrypt(response.emailDestinatario) : '',
            descricao: response.descricao ? decrypt(response.descricao) : '',
            responsavelResposta: response.responsavelResposta ? decrypt(response.responsavelResposta) : '',
            resposta: response.resposta ? decrypt(response.resposta) : '',
        }

        return decryptedResponse;
    } catch (error) {
        console.log(error);
    }
  }, []);

  const updateMonitoramento = useCallback(async (id, data) => {
    try {
      if (!data.dilacaoPrazoResposta || Number(data.dilacaoPrazoResposta) === 0) {
        const newData = {
          ...data,
          novaDataResposta: '',
        }

        const response = await monitoramentoService.update(id, newData);
        
        return response;
      } else {
        const response = await monitoramentoService.update(id, data);

        return response;
      }
    } catch (error) {
        console.log(error);
    }
  }, []);

  const deleteMonitoramento = useCallback(async (id) => {
    try {
        const response = await monitoramentoService.delete(id);

        if (!response.ok) {
            throw new Error('Ocorreu um erro inesperado');
        }
    } catch (error) {
        console.log(error);
    }
  }, []);

  const createMonitoramento = useCallback(async (data) => {
    try {
      if (!data.dilacaoPrazoResposta) {
        const newData = {
          ...data,
          novaDataResposta: '',
        }

        const response = await monitoramentoService.create(newData);

        if (!response.ok) {
          throw new Error('Ocorreu um erro inesperado');
        }
      } else {
        const response = await monitoramentoService.create(data);

        if (!response.ok) {
            throw new Error('Ocorreu um erro inesperado');
        }
      }
    } catch (error) {
        console.log(error);
    }
  }, []);

  async function exportExcel() {
    async function getAllMonitoramentosAndExport() {
      try {
        const fileName = `monitoramento-${formatDate(new Date()).replace(/\//g, '-')}`
        const exportType = 'xls';

        const response = await getAllMonitoramentos(1, 100000);
    
        const data = await response.monitoramentos.map(monitoramento => {
          return {
            'IDENTIFICADOR': monitoramento.identificador ? monitoramento.identificador : '',
            'DATA DE RECEBIMENTO': monitoramento.dataRecebimento ? formatDate(monitoramento.dataRecebimento) : '',
            'TIPO DE DOCUMENTO': monitoramento.tipoDocumento ? monitoramento.tipoDocumento : '',
            'USUÁRIO': monitoramento.usuario ? decrypt(monitoramento.usuario) : '',
            'ÓRGÃO': monitoramento.remetente ? decrypt(monitoramento.remetente) : '',
            'OUTRO ÓRGÃO?': monitoramento.outroRemetente ? decrypt(monitoramento.outroRemetente) : '',
            'E-MAIL DO ÓRGÃO': monitoramento.emailRemetente ? decrypt(monitoramento.emailRemetente) : '',
            'DESTINATÁRIO': monitoramento.unidade ? monitoramento.unidade : '',
            'REGIÃO': monitoramento.regiao ? monitoramento.regiao : '',
            'E-MAIL DESTINATÁRIO': monitoramento.emailDestinatario ? decrypt(monitoramento.emailDestinatario) : '',
            'TIPO DA SOLICITAÇÃO': monitoramento.tipoSolicitacao ? monitoramento.tipoSolicitacao : '',
            'DOCUMENTO': monitoramento.documento ? monitoramento.documento : '',
            'IDENTIFICAÇÃO DO DOCUMENTO': monitoramento.idDocumento ? monitoramento.idDocumento : '',
            'IDENTIFICAÇÃO DO INQUÉRITO': monitoramento.idInquerito ? monitoramento.idInquerito : '',
            'DESCRIÇÃO': monitoramento.descricao ? decrypt(monitoramento.descricao) : '',
            'DATA PARA RESPOSTA INTERNA': monitoramento.dataRespostaInterna ? formatDate(monitoramento.dataRespostaInterna) : '',
            'PRAZO REAL (EM DIAS)': monitoramento.prazoReal ? monitoramento.prazoReal : '',
            'DATA MÁXIMA REAL': monitoramento.dataMaxReal ? formatDate(monitoramento.dataMaxReal) : '',
            'DILAÇÃO DO PRAZO (EM DIAS)': monitoramento.dilacaoPrazoResposta ? monitoramento.dilacaoPrazoResposta : '',
            'NOVA DATA PARA RESPOSTA': monitoramento.novaDataResposta ? formatDate(monitoramento.novaDataResposta) : '',
            'SITUAÇÃO': monitoramento.situacao ? monitoramento.situacao : '',
            'DATA DE ENVIO DA RESPOSTA': monitoramento.dataEnvioResposta ? formatDate(monitoramento.dataEnvioResposta) : '',
            'RESPONSÁVEL PELA RESPOSTA': monitoramento.responsavelResposta ? decrypt(monitoramento.responsavelResposta) : '',
            'NÚMERO DE OFÍCIO DA RESPOSTA': monitoramento.numeroOficioResposta ? monitoramento.numeroOficioResposta : '',
            'RESPOSTA': monitoramento.resposta ? decrypt(monitoramento.resposta) : '',
          };
        });
    
        exportFromJSON({ data, fileName, exportType })
      } catch (error) {
        console.log(error);
      }
    }

    getAllMonitoramentosAndExport();
  }

  const initialValues = {
    identificador: '',
    dataRecebimento: startOfToday(),
    tipoDocumento: '',
    usuario: '',
    nomeUsuario: '',
    remetente: '',
    outroRemetente: '',
    emailRemetente: '',
    unidade: '',
    regiao: '',
    emailDestinatario: '',
    tipoSolicitacao: '',
    documento: '',
    idDocumento: '',
    idInquerito: '',
    descricao: '',
    dataRespostaInterna: '',
    prazoReal: 0,
    dataMaxReal: '',
    dilacaoPrazoResposta: 0,
    novaDataResposta: '',
    situacao: 'Pendente dentro do prazo',
    dataEnvioResposta: '',
    responsavelResposta: '',
    numeroOficioResposta: '',
    resposta: '',
  };

  return (
    <MonitoramentoContext.Provider
      value={{
        getAllMonitoramentos,
        getMonitoramentoById,
        updateMonitoramento,
        deleteMonitoramento,
        createMonitoramento,
        exportExcel,
        initialValues,
      }}
    >
      {children}
    </MonitoramentoContext.Provider>
  );
}

export const useMonitoramento = () => useContext(MonitoramentoContext);
